import * as React from 'react'
// Ellipsis / kebab use Lucide, not Phosphor — Phosphor's dots render too thin to
// read as an overflow affordance (the documented fb-ui icon exception).
import { Ellipsis } from 'lucide-react'
import { Button } from '../core/button'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../core/dropdown-menu'

/**
 * WidgetMenu — the shared ⋯ overflow menu in a dashboard widget's header. Wraps
 * the ellipsis trigger + `DropdownMenu` so every widget (Pinned, Recently
 * Visited, Upcoming, the performance widgets) renders the same affordance.
 * Compose the items per widget — "View all", "Edit pins", "Hide widget", etc.
 *
 *   <WidgetMenu items={[
 *     { key: 'view-all', label: 'View all', icon: <ArrowRight />, onSelect: onViewAll },
 *     { key: 'hide', label: 'Hide widget', icon: <EyeSlash />, onSelect: onHide },
 *   ]} />
 */

export interface WidgetMenuItem {
  /** Stable key. */
  key: string
  label: React.ReactNode
  icon?: React.ReactNode
  onSelect: () => void
}

export interface WidgetMenuProps {
  items: WidgetMenuItem[]
  /** Accessible name for the trigger. */
  label?: string
}

export const WidgetMenu = ({ items, label = 'Widget options' }: WidgetMenuProps) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="neutral" iconOnly size="sm" aria-label={label}>
        <Ellipsis />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      {items.map((item) => (
        <DropdownMenuItem
          key={item.key}
          icon={item.icon}
          onSelect={() => item.onSelect()}
        >
          {item.label}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
)
WidgetMenu.displayName = 'WidgetMenu'
